import { z } from "zod";

import { paginationMetaDtoSchema } from "@nasam/kernel";

const id = z.string().min(1);

// A real ISO date-time (e.g. "2026-07-19T10:15:30.123Z"), not just any
// string: the screens format it, and an unparseable value would crash the
// render instead of showing the load-error state.
const isoDateTime = z.string().datetime({ offset: true });

const namedRefDtoSchema = z.object({
  id,
  nameEn: z.string(),
  nameAr: z.string(),
});

/** `GET /driver-assignments/drivers` row, also nested in create's response. */
export const assignableDriverDtoSchema = z.object({
  id,
  username: z.string(),
  nameEn: z.string(),
  nameAr: z.string(),
  driverProfile: z.object({ code: z.string() }).nullable().optional(),
});

export const assignableDriversResponseDtoSchema = z.object({
  data: z.array(assignableDriverDtoSchema),
});

/** `GET /driver-assignments/assets` row, also nested in create's response
 *  (which adds `accountId`). */
export const assignableAssetDtoSchema = z.object({
  id,
  code: z.string(),
  nameEn: z.string(),
  nameAr: z.string(),
  branch: namedRefDtoSchema,
});

export const assignableAssetsResponseDtoSchema = z.object({
  data: z.array(assignableAssetDtoSchema),
});

/** `POST /driver-assignments` and `PATCH /driver-assignments/{id}` body;
 *  the server sets the assignment date. */
export const driverAssignmentCreateDtoSchema = z.object({
  driverId: id,
  assetId: id,
});

export const driverAssignmentDtoSchema = z.object({
  id,
  driverId: id,
  assetId: id,
  assignedAt: isoDateTime,
  endedAt: isoDateTime.nullable(),
  driver: assignableDriverDtoSchema,
  asset: assignableAssetDtoSchema.extend({ accountId: z.string().optional() }),
});

export const driverAssignmentResponseDtoSchema = z.object({
  data: driverAssignmentDtoSchema,
  message: z.string().optional(),
});

/** `GET /driver-assignments` row (NASAM-284): one per active assignment. */
export const driverAssignmentListItemDtoSchema = z.object({
  id,
  assignedAt: isoDateTime,
  driver: assignableDriverDtoSchema,
  asset: assignableAssetDtoSchema,
});

/** `GET /driver-assignments/{id}` (NASAM-779): same row as the list. */
export const driverAssignmentDetailResponseDtoSchema = z.object({
  data: driverAssignmentListItemDtoSchema,
});

export const driverAssignmentListResponseDtoSchema = z.object({
  data: z.array(driverAssignmentListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

/** `DELETE /driver-assignments/{id}` (NASAM-286). */
export const driverAssignmentDeleteResponseDtoSchema = z.object({
  message: z.string().optional(),
});

/** `GET /driver-assignments/history` row (NASAM-287): flat, read-only. Also
 *  carries ids (assignment, account, assignedBy, actionBy), assignedByName
 *  and a status (Active / Ended) the History screen doesn't show. */
export const driverAssignmentHistoryItemDtoSchema = z.object({
  id,
  driverId: id,
  driverCode: z.string().nullable().optional(),
  driverNameEn: z.string(),
  driverNameAr: z.string(),
  assetId: id,
  assetNameEn: z.string(),
  assetNameAr: z.string(),
  action: z.enum(["Created", "Updated", "Removed"]),
  status: z.string().optional(),
  assignedAt: isoDateTime,
  actionAt: isoDateTime,
});

export const driverAssignmentHistoryResponseDtoSchema = z.object({
  data: z.array(driverAssignmentHistoryItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

/** The existing `GET /branches` (shared with the Assets screens); only the
 *  fields the Branch filter needs. */
export const branchOptionsResponseDtoSchema = z.object({
  data: z.array(namedRefDtoSchema.extend({ isActive: z.boolean().optional() })),
});

export type AssignableDriverDto = z.infer<typeof assignableDriverDtoSchema>;
export type AssignableAssetDto = z.infer<typeof assignableAssetDtoSchema>;
export type DriverAssignmentDto = z.infer<typeof driverAssignmentDtoSchema>;
export type DriverAssignmentHistoryItemDto = z.infer<
  typeof driverAssignmentHistoryItemDtoSchema
>;
export type DriverAssignmentListItemDto = z.infer<
  typeof driverAssignmentListItemDtoSchema
>;
